import { type LoginSuccess, isMfaChallenge, makeLoginForm, makeMfaVerifyForm } from "@seagames/contracts";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { type FormEvent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
	AuthFrame,
	AuthHeading,
	Checkbox,
	Field,
	FormAlert,
	KeyGlyph,
	LinkButton,
	PRIMARY_BUTTON,
	PasswordToggle,
	SubmitButton,
	UserGlyph,
	fieldIconClass,
} from "../components/auth/auth-frame";
import { ApiError } from "../lib/api/client";
import { errorMessage, isTransientError, supportRequestIdOf } from "../lib/api/messages";
import { forgetIdentifier, readRememberedIdentifier, rememberIdentifier } from "../lib/auth/remembered-identifier";
import { acceptLogin, login, verifyMfa } from "../lib/auth/session";

/**
 * Sign-in: credentials → (TOTP code when the role needs 2FA) → in. The card
 * is GMS's (icons in the fields, "Remember me", the chevron button), so every
 * platform login looks the same.
 *
 * Always AS the medal desk (`login()` asks for MEDAL_DESK by name): an account
 * without that role gets its own sentence, not the API's generic one.
 *
 * Enrolment is NOT done here: the Medal desk role needs 2FA, and when the API
 * answers `mfa_stage: "enroll"` the desk member is sent to GMS to set it up
 * first (VITE_GMS_URL). MSES keeps one job. (Copied from OVR's sign-in.)
 *
 * `redirect` is validated as a same-origin path — an absolute URL here would
 * be an open redirect.
 */
const loginSearch = z.object({
	redirect: z
		.string()
		.optional()
		.transform((v) => (v && v.startsWith("/") && !v.startsWith("//") ? v : undefined)),
	reason: z.enum(["revoked", "expired", "user"]).optional(),
});

export const Route = createFileRoute("/login")({
	validateSearch: loginSearch,
	component: LoginPage,
});

const GMS_URL: string = (import.meta.env.VITE_GMS_URL as string | undefined) ?? "http://localhost:5173";

type Stage =
	| { kind: "credentials" }
	| { kind: "mfa-verify"; ticket: string; name: string }
	| { kind: "mfa-enroll"; name: string };

function LoginPage() {
	const { t } = useTranslation("auth");
	const { redirect, reason } = Route.useSearch();
	const navigate = useNavigate();
	const [stage, setStage] = useState<Stage>({ kind: "credentials" });

	const finish = (result: LoginSuccess) => {
		acceptLogin(result);
		if (result.must_change_password) {
			void navigate({ to: "/change-password", search: { redirect }, replace: true });
		} else {
			void navigate({ to: redirect ?? "/", replace: true });
		}
	};

	return (
		<AuthFrame footer={stage.kind === "credentials" ? t("login.help") : undefined}>
			{stage.kind === "credentials" && <CredentialsStep reason={reason} onStage={setStage} onDone={finish} />}
			{stage.kind === "mfa-verify" && (
				<MfaVerifyStep
					ticket={stage.ticket}
					name={stage.name}
					onDone={finish}
					onRestart={() => setStage({ kind: "credentials" })}
				/>
			)}
			{stage.kind === "mfa-enroll" && (
				<>
					<AuthHeading title={t("mfa.enroll.title")}>{t("mfa.enroll.body", { name: stage.name })}</AuthHeading>
					<a href={GMS_URL} target="_blank" rel="noreferrer" className={PRIMARY_BUTTON}>
						{t("mfa.enroll.openGms")}
					</a>
					<LinkButton onClick={() => setStage({ kind: "credentials" })}>{t("mfa.backToSignIn")}</LinkButton>
				</>
			)}
		</AuthFrame>
	);
}

function ServerError({ error }: { error: unknown }) {
	const { t } = useTranslation("auth");
	const requestId = supportRequestIdOf(error);
	return (
		<FormAlert tone={isTransientError(error) ? "warn" : "error"}>
			{error instanceof ApiError && error.code === "ROLE_NOT_HELD" ? t("login.notDesk") : errorMessage(error)}
			{requestId && (
				<span className="mt-[0.25rem] block font-normal text-[0.75rem] opacity-80">
					{t("error.requestId")} <code className="font-mono">{requestId}</code>
				</span>
			)}
		</FormAlert>
	);
}

function CredentialsStep({
	reason,
	onStage,
	onDone,
}: {
	reason: "revoked" | "expired" | "user" | undefined;
	onStage: (s: Stage) => void;
	onDone: (r: LoginSuccess) => void;
}) {
	const { t } = useTranslation("auth");
	const schema = useMemo(
		() =>
			makeLoginForm({
				identifierRequired: t("validation.identifierRequired"),
				passwordRequired: t("validation.passwordRequired"),
			}),
		[t],
	);
	const [remembered] = useState(readRememberedIdentifier);
	const [identifier, setIdentifier] = useState(remembered);
	const [password, setPassword] = useState("");
	const [remember, setRemember] = useState(() => remembered !== "");
	const [show, setShow] = useState(false);
	const [fieldErrors, setFieldErrors] = useState<{ identifier?: string; password?: string }>({});
	const [serverError, setServerError] = useState<unknown>(null);
	const [pending, setPending] = useState(false);

	const submit = async (e: FormEvent) => {
		e.preventDefault();
		const parsed = schema.safeParse({ identifier, password });
		if (!parsed.success) {
			const f = parsed.error.flatten().fieldErrors;
			setFieldErrors({ identifier: f.identifier?.[0], password: f.password?.[0] });
			return;
		}
		setFieldErrors({});
		setServerError(null);
		setPending(true);
		try {
			const result = await login(parsed.data);
			// Only once the server has accepted it — remembering a typo would hand
			// the typo back on every future visit.
			if (remember) rememberIdentifier(parsed.data.identifier);
			else forgetIdentifier();
			if (isMfaChallenge(result)) {
				onStage(
					result.mfa_stage === "verify"
						? { kind: "mfa-verify", ticket: result.mfa_ticket, name: result.user.name }
						: { kind: "mfa-enroll", name: result.user.name },
				);
				return;
			}
			onDone(result);
		} catch (err) {
			setServerError(err);
		} finally {
			setPending(false);
		}
	};

	return (
		<>
			<AuthHeading title={t("login.title")}>{t("login.subtitle")}</AuthHeading>
			{reason && reason !== "user" && !serverError && <FormAlert tone="info">{t(`reasons.${reason}`)}</FormAlert>}
			{serverError != null && <ServerError error={serverError} />}
			<form onSubmit={submit} noValidate className="grid gap-[1rem]">
				<Field
					label={t("login.identifier.label")}
					name="identifier"
					autoComplete="username"
					autoCapitalize="none"
					spellCheck={false}
					autoFocus
					placeholder={t("login.identifier.placeholder")}
					icon={<UserGlyph className={fieldIconClass} />}
					value={identifier}
					onChange={(e) => setIdentifier(e.target.value)}
					error={fieldErrors.identifier}
				/>
				<Field
					label={t("login.password.label")}
					name="password"
					type={show ? "text" : "password"}
					autoComplete="current-password"
					placeholder={t("login.password.placeholder")}
					icon={<KeyGlyph className={fieldIconClass} />}
					value={password}
					onChange={(e) => setPassword(e.target.value)}
					error={fieldErrors.password}
					trailing={
						<PasswordToggle
							shown={show}
							onToggle={() => setShow((s) => !s)}
							showLabel={t("login.password.show")}
							hideLabel={t("login.password.hide")}
						/>
					}
				/>
				<Checkbox checked={remember} onChange={setRemember}>
					{t("login.rememberMe")}
				</Checkbox>
				<SubmitButton pending={pending} chevron>
					{t("login.submit")}
				</SubmitButton>
			</form>
		</>
	);
}

function MfaVerifyStep({
	ticket,
	name,
	onDone,
	onRestart,
}: {
	ticket: string;
	name: string;
	onDone: (r: LoginSuccess) => void;
	onRestart: () => void;
}) {
	const { t } = useTranslation("auth");
	const schema = useMemo(() => makeMfaVerifyForm({ codeRequired: t("validation.codeRequired") }), [t]);
	const [code, setCode] = useState("");
	const [fieldError, setFieldError] = useState<string | undefined>();
	const [serverError, setServerError] = useState<unknown>(null);
	const [pending, setPending] = useState(false);

	const submit = async (e: FormEvent) => {
		e.preventDefault();
		const parsed = schema.safeParse({ mfa_ticket: ticket, code });
		if (!parsed.success) {
			setFieldError(parsed.error.flatten().fieldErrors.code?.[0] ?? t("validation.codeRequired"));
			return;
		}
		setFieldError(undefined);
		setServerError(null);
		setPending(true);
		try {
			onDone(await verifyMfa(parsed.data));
		} catch (err) {
			setServerError(err);
		} finally {
			setPending(false);
		}
	};

	return (
		<>
			<AuthHeading title={t("mfa.verify.title")}>{t("mfa.verify.subtitle", { name })}</AuthHeading>
			{serverError != null && <ServerError error={serverError} />}
			<form onSubmit={submit} noValidate className="grid gap-[1rem]">
				<Field
					label={t("mfa.verify.code.label")}
					hint={t("mfa.verify.code.hint")}
					error={fieldError}
					name="code"
					inputMode="numeric"
					autoComplete="one-time-code"
					autoFocus
					mono
					maxLength={12}
					value={code}
					onChange={(e) => setCode(e.target.value)}
				/>
				<SubmitButton pending={pending}>{t("mfa.verify.submit")}</SubmitButton>
				<LinkButton onClick={onRestart} disabled={pending}>
					{t("mfa.backToSignIn")}
				</LinkButton>
			</form>
		</>
	);
}
